import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  MoreVertical,
  Coins,
  Zap,
  Flame,
  Lock,
  Unlock,
  Crown,
  Shield,
  ShieldCheck,
  ShieldOff,
  PenTool,
  UserCheck,
  Download,
  X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * UserActionsMenu — Feb 2026.
 *
 * Consolidated per-user kebab menu on the Admin Panel user list. Wires
 * up every grant/allocation the primary admin needs into a single
 * dropdown so the row stays tidy.
 *
 * Actions:
 *   • Change role → Reader, Scribe/Writer, King, Backup Admin
 *   • Award Coins (additive)   → POST /api/admin/grant-coins
 *   • Award XP    (additive)   → POST /api/admin/grant-xp
 *   • Set Streak  (absolute)   → POST /api/admin/users/{uid}/gift-streak
 *   • Grant / Revoke Vault     → POST /api/admin/grant-vault | revoke-vault
 *   • Grant / Revoke Premium   → POST /api/admin/grant-premium | revoke-premium
 *   • Grant / Revoke Offline   → POST /api/admin/grant-offline | revoke-offline
 *
 * Props:
 *   user       — the user object from /api/admin/users
 *   onChanged  — optional callback fired after any mutation so the
 *                parent can refetch its list
 */
const UserActionsMenu = ({ user, onChanged }) => {
  const [action, setAction] = useState(null); // one of the ACTION_* keys
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const uid = user?.user_id;
  const label = user?.name || user?.email || uid;

  const close = () => {
    setAction(null);
    setAmount('');
    setNote('');
    setBusy(false);
  };

  const post = async (path, body) => {
    setBusy(true);
    try {
      const res = await authFetch(`${BACKEND_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Action failed');
        setBusy(false);
        return false;
      }
      return true;
    } catch (e) {
      toast.error(e.message || 'Network error');
      setBusy(false);
      return false;
    }
  };

  // ── Handlers ───────────────────────────────────────────────────────
  const doRoleChange = async (role, humanLabel) => {
    if (!window.confirm(`Set ${label}'s role to "${humanLabel}"?`)) return;
    const ok = await post(`/api/admin/users/${uid}/set-role`, { role });
    if (ok) {
      toast.success(`${label} is now a ${humanLabel}.`);
      onChanged?.();
    }
  };

  const doAmountAction = async (path, unit) => {
    const n = parseInt(amount, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error(`Enter a positive ${unit} amount`);
      return;
    }
    const ok = await post(path, { target_user_id: uid, amount: n, note: note.trim() || undefined });
    if (ok) {
      toast.success(`Granted ${n.toLocaleString()} ${unit} to ${label}.`);
      close();
      onChanged?.();
    }
  };

  const doStreakSet = async () => {
    const days = parseInt(amount, 10);
    if (!Number.isFinite(days) || days < 1 || days > 365) {
      toast.error('Days must be between 1 and 365');
      return;
    }
    if (!note.trim()) {
      toast.error('Please add a short reason (goes into the audit log)');
      return;
    }
    const ok = await post(`/api/admin/users/${uid}/gift-streak`, { days, reason: note.trim() });
    if (ok) {
      toast.success(`Streak set to ${days} day${days === 1 ? '' : 's'} for ${label}.`);
      close();
      onChanged?.();
    }
  };

  const doPremiumGrant = async () => {
    const days = parseInt(amount, 10);
    if (!Number.isFinite(days) || days < 1) {
      toast.error('Enter a positive number of days');
      return;
    }
    const ok = await post('/api/admin/grant-premium', {
      target_user_id: uid,
      tier: 'premium',
      days,
      note: note.trim() || undefined,
    });
    if (ok) {
      toast.success(`Premium granted for ${days} day${days === 1 ? '' : 's'}.`);
      close();
      onChanged?.();
    }
  };

  const doSimpleGrant = async (path, verb) => {
    const ok = await post(path, { target_user_id: uid });
    if (ok) {
      toast.success(`${verb} ${label}.`);
      onChanged?.();
    }
  };

  // Root admins can't be modified via the menu — the /set-role endpoint
  // enforces this too, but hiding the trigger avoids a confusing UX.
  const isRootAdmin = user?.role === 'admin' && !user?.pre_admin_role;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-gray-300 hover:text-white hover:bg-slate-700"
            data-testid={`user-actions-menu-trigger-${uid}`}
            disabled={isRootAdmin}
            title={isRootAdmin ? 'Root admin — protected' : 'User actions'}
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-64 bg-slate-900 text-white border-slate-700"
          data-testid={`user-actions-menu-${uid}`}
        >
          <DropdownMenuLabel className="text-xs text-gray-400 truncate">
            {label}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-700" />

          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">
            Change role
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => doRoleChange('user', 'Reader')}
            data-testid={`user-actions-role-user-${uid}`}
          >
            <UserCheck className="w-4 h-4 mr-2 text-blue-400" /> Reader
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => doRoleChange('writer', 'Scribe')}
            data-testid={`user-actions-role-writer-${uid}`}
          >
            <PenTool className="w-4 h-4 mr-2 text-purple-400" /> Scribe / Writer
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => doRoleChange('king', 'King (co-admin)')}
            data-testid={`user-actions-role-king-${uid}`}
          >
            <Shield className="w-4 h-4 mr-2 text-cyan-400" /> King / Co-admin
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => doRoleChange('admin', 'Backup Admin')}
            data-testid={`user-actions-role-admin-${uid}`}
            className="text-red-400 focus:text-red-300"
          >
            <ShieldCheck className="w-4 h-4 mr-2" /> Backup Admin (full)
          </DropdownMenuItem>

          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">
            Awards (additive)
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={() => setAction('coins')} data-testid={`user-actions-coins-${uid}`}>
            <Coins className="w-4 h-4 mr-2 text-yellow-400" /> Award Coins
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAction('xp')} data-testid={`user-actions-xp-${uid}`}>
            <Zap className="w-4 h-4 mr-2 text-emerald-400" /> Award XP
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAction('streak')} data-testid={`user-actions-streak-${uid}`}>
            <Flame className="w-4 h-4 mr-2 text-orange-400" /> Set Streak (days)
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAction('premium')} data-testid={`user-actions-premium-${uid}`}>
            <Crown className="w-4 h-4 mr-2 text-amber-400" /> Grant Premium (days)
          </DropdownMenuItem>

          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">
            Access flags
          </DropdownMenuLabel>
          {user?.vault_access_granted ? (
            <DropdownMenuItem
              onClick={() => doSimpleGrant('/api/admin/revoke-vault', 'Revoked Vault for')}
              data-testid={`user-actions-vault-revoke-${uid}`}
              className="text-orange-300"
            >
              <Unlock className="w-4 h-4 mr-2" /> Revoke Vault
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => doSimpleGrant('/api/admin/grant-vault', 'Granted Vault to')}
              data-testid={`user-actions-vault-grant-${uid}`}
              className="text-fuchsia-300"
            >
              <Lock className="w-4 h-4 mr-2" /> Grant Vault
            </DropdownMenuItem>
          )}
          {user?.offline_library_granted ? (
            <DropdownMenuItem
              onClick={() => doSimpleGrant('/api/admin/revoke-offline', 'Revoked Offline for')}
              data-testid={`user-actions-offline-revoke-${uid}`}
              className="text-orange-300"
            >
              <X className="w-4 h-4 mr-2" /> Revoke Offline Library
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => doSimpleGrant('/api/admin/grant-offline', 'Granted Offline to')}
              data-testid={`user-actions-offline-grant-${uid}`}
              className="text-green-300"
            >
              <Download className="w-4 h-4 mr-2" /> Grant Offline Library
            </DropdownMenuItem>
          )}
          {(user?.subscription_tier && user.subscription_tier !== 'free') && (
            <DropdownMenuItem
              onClick={() => doSimpleGrant('/api/admin/revoke-premium', 'Revoked Premium from')}
              data-testid={`user-actions-premium-revoke-${uid}`}
              className="text-orange-300"
            >
              <ShieldOff className="w-4 h-4 mr-2" /> Revoke Premium
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Amount + note input modal used by coins / xp / streak / premium */}
      <Dialog open={!!action} onOpenChange={(o) => !o && close()}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid={`user-actions-modal-${uid}`}>
          <DialogHeader>
            <DialogTitle>
              {action === 'coins' && `Award coins to ${label}`}
              {action === 'xp' && `Award XP to ${label}`}
              {action === 'streak' && `Set streak for ${label}`}
              {action === 'premium' && `Grant Premium to ${label}`}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {action === 'coins' && 'Coins are added to the user\'s current balance.'}
              {action === 'xp' && 'XP is added to the user\'s current total (updates level bars).'}
              {action === 'streak' && 'Sets the streak to the specified number of days (never downgrades).'}
              {action === 'premium' && 'Extends premium_until by N days from today (never shortens).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                {action === 'streak' || action === 'premium' ? 'Days' : 'Amount'}
              </label>
              <Input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={action === 'streak' ? 'e.g. 7' : action === 'premium' ? 'e.g. 30' : 'e.g. 500'}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid={`user-actions-amount-input-${uid}`}
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs text-gray-400 block mb-1">
                {action === 'streak' ? 'Reason (required, audit log)' : 'Note (optional)'}
              </label>
              <Input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={action === 'streak' ? 'e.g. Compensation for outage' : 'Optional audit note'}
                maxLength={240}
                className="bg-slate-800 border-slate-700 text-white"
                data-testid={`user-actions-note-input-${uid}`}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={close}
              className="border-slate-700 text-white"
              data-testid={`user-actions-cancel-${uid}`}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (action === 'coins') doAmountAction('/api/admin/grant-coins', 'coins');
                else if (action === 'xp') doAmountAction('/api/admin/grant-xp', 'XP');
                else if (action === 'streak') doStreakSet();
                else if (action === 'premium') doPremiumGrant();
              }}
              disabled={busy || !amount}
              className="bg-red-600 hover:bg-red-700"
              data-testid={`user-actions-submit-${uid}`}
            >
              {busy ? 'Working…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserActionsMenu;
